import { useMutation, useQueryClient } from '@tanstack/react-query';

import { testAiProviderConnection } from '@/lib/api/generated/api';

import { toAiProviderError } from '../errors';
import { aiProviderKeys } from '../queryKeys';
import type { AiProviderTestResult } from '../types';

/** What to check, when it is not simply what the provider already has stored. */
export interface TestAiProviderVariables {
  id: string;
  /**
   * Candidate values an admin has typed but not saved. Each omitted field falls
   * back to the stored one, so `undefined` here is exactly the old behaviour.
   */
  overrides?: { base_url?: string; token?: string };
}

/**
 * Run a live connection test for one AI Provider (spec FR-008).
 *
 * The backend updates the provider's `connection_status`/`connection_message`/
 * `connection_checked_at` as a side effect of this call, so the list is
 * invalidated on success to pick up the new badge state — the test result
 * itself (`ok`/`message`) is what the caller surfaces as a toast.
 *
 * That side effect is the reason `overrides` exists and the reason the backend
 * does not persist an overridden result: a form still holding unsaved edits
 * needs to know whether *those* credentials work, but the badge on the list must
 * keep describing the pair the provider will actually run with.
 */
export function useTestAiProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      overrides,
    }: TestAiProviderVariables): Promise<AiProviderTestResult> => {
      const response = await testAiProviderConnection(
        id,
        overrides
          ? { base_url: overrides.base_url, token: overrides.token }
          : undefined,
      );
      if (response.status === 200) return response.data;
      throw toAiProviderError(response.status, response.data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: aiProviderKeys.all() });
    },
  });
}
